import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ShortType } from 'shared/util-common';

import {
  ActionType,
  ActivityDefinitionDto,
  ActivityRequestResourceType,
  Attachment,
  BibliographyDto,
  CodeableConceptDto,
  CodeableConceptValueDto,
  CodingValue,
  ComponentDto,
  ComponentLinkDto,
  ComponentState,
  ComponentTranslationDto,
  ComponentType,
  ConditionDefinitionDto,
  FindingDefinitionDto,
  FindingType,
  FlowchartActionDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartNodeStyleDto,
  FlowchartSelectionBehaviorDto,
  FlowchartStartNodeDto,
  FlowchartTextBlockDto,
  GuidelineActionDto,
  GuidelineActionPriority,
  GuidelineActionSignificance,
  GuidelineChapterType,
  GuidelineComponentDto,
  GuidelineComponentType,
  GuidelineConditionDto,
  GuidelineCriterionDto,
  GuidelineObservationDto,
  NewGuidelineChapter,
  ObservationCategory,
  ObservationDefinitionRegionalTranslationDto,
  ObservationPermittedDataType,
  ProposedComponentDto,
  ReferenceRangeDto,
  RegionalTranslationDto,
  SelectionBehavior,
  TaxonomyDto,
  UnitDto,
  UnitType,
} from '@treetop/shared/model';

export function create_UUID(): string {
  let dt = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function parseControlError(key: string, control: AbstractControl): Record<string, ValidationErrors> {
  const res: Record<string, ValidationErrors> = {};

  if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
    const errors = getAllFormErrors(control, key);
    if (Object.keys(errors).length > 0) {
      Object.assign(res, errors);
    }
  } else if (control.errors) {
    res[key] = control.errors;
  }

  return res;
}

export function getAllFormErrors(form: UntypedFormGroup | UntypedFormArray, path = ''): Record<string, ValidationErrors> {
  const res: Record<string, ValidationErrors> = {};

  if (form instanceof UntypedFormGroup) {
    for (const key of Object.keys(form.controls)) {
      const keyValue = path ? `${path}.${key}` : key;
      Object.assign(res, parseControlError(`${keyValue}`, form.controls[key]));
    }
  }

  if (form instanceof UntypedFormArray) {
    for (let i = 0; i < form.controls.length; i++) {
      const keyValue = path ? `${path}.${i}` : i;
      Object.assign(res, parseControlError(`${keyValue}`, form.controls[i]));
    }
  }

  return res;
}

function setAlternativeTranslations(
  component: ComponentDto,
  alternativeTranslations: FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>,
): void {
  const regionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);

  component?.alternativeTranslations?.forEach((altTranslations) => {
    altTranslations.regionalTranslations?.forEach(() => {
      createRegionalTranslationsForm(component.type, regionalTranslations);
    });

    if (component.type === ComponentType.ObservationDefinition) {
      alternativeTranslations.push(
        new UntypedFormGroup({
          languageCode: new FormControl<string | null>(null, []),
          title: new FormControl<string | null>(null, []),
          description: new FormControl<string | null>(null, []),
          unit: new FormGroup<ControlsOf<UnitDto>>({
            id: new FormControl<string | null>(null, []),
            unitCategoryId: new FormControl<string | null>(null, []),
            unitCategoryName: new FormControl<string | null>(null, []),
            name: new FormControl<string | null>(null, []),
            abbreviation: new FormControl<string | null>(null, []),
            type: new FormControl<UnitType | null>(null, []),
          }),
          unitId: new FormControl<string | null>(null, []),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          regionalTranslations: regionalTranslations,
        }),
      );
    } else {
      alternativeTranslations.push(
        new FormGroup<ControlsOf<ComponentTranslationDto>>({
          languageCode: new UntypedFormControl(),
          title: new UntypedFormControl(),
          description: new UntypedFormControl(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          regionalTranslations: regionalTranslations,
        }),
      );
    }
  });
}

function setRegionalTranslations(component: ComponentDto, regionalTranslations: FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>) {
  component?.regionalTranslations?.forEach(() => {
    createRegionalTranslationsForm(component.type, regionalTranslations);
  });
}

function createRegionalTranslationsForm(
  componentType: ComponentType,
  regionalTranslations: FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>,
) {
  if (componentType === ComponentType.ObservationDefinition) {
    regionalTranslations.push(
      new FormGroup<ControlsOf<ObservationDefinitionRegionalTranslationDto>>({
        regionCode: new FormControl<string | null>(null, []),
        title: new FormControl<string | null>(null, []),
        description: new FormControl<string | null>(null, []),
        unit: new FormGroup<ControlsOf<UnitDto>>({
          id: new FormControl<string | null>(null, []),
          unitCategoryId: new FormControl<string | null>(null, []),
          unitCategoryName: new FormControl<string | null>(null, []),
          name: new FormControl<string | null>(null, []),
          abbreviation: new FormControl<string | null>(null, []),
          type: new FormControl<UnitType | null>(null, []),
        }),
        unitId: new FormControl<string | null>(null, []),
      }),
    );
  } else {
    regionalTranslations.push(
      new FormGroup<ControlsOf<RegionalTranslationDto>>({
        regionCode: new FormControl<string | null>(null, []),
        title: new FormControl<string | null>(null, []),
        description: new FormControl<string | null>(null, []),
      }),
    );
  }
}

export function setCodeableConceptValues(component: CodeableConceptDto, target: FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>) {
  const pushFormGroupToTarget = (value: CodeableConceptValueDto & { $ref: string }, subTarget: UntypedFormArray) => {
    const alternativeTranslations = new FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>([]);
    const regionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);
    if (value) {
      setAlternativeTranslations(value, alternativeTranslations);
      setRegionalTranslations(value, regionalTranslations);
    }
    const targetFormGroup = new FormGroup<ControlsOf<CodeableConceptValueDto>>({
      codeableConceptCode: new UntypedFormControl(),
      code: new UntypedFormControl(),
      order: new UntypedFormControl(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      children: new UntypedFormArray([]),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      parents: new UntypedFormArray([]),
      defaultLanguageCode: new UntypedFormControl(),
      title: new UntypedFormControl(),
      description: new UntypedFormControl(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      alternativeTranslations: alternativeTranslations,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      regionalTranslations: regionalTranslations,
      hasParents: new UntypedFormControl(),
      findingDefinitionCode: new UntypedFormControl(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      codings: new FormControl<CodingValue[] | null>([]),
    });
    targetFormGroup.patchValue(value);
    if (value.parents?.length > 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pushFormGroupToTarget({ hasParents: true }, targetFormGroup.get('parents') as UntypedFormArray);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      targetFormGroup.get('hasParents').patchValue(true);
    }
    if (value.children?.length > 0) {
      for (const val of value.children) {
        const childAlternativeTranslations = new FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>([]);
        const chilRegionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);
        if (val) {
          setAlternativeTranslations(component, childAlternativeTranslations);
          setRegionalTranslations(component, chilRegionalTranslations);
        }
        pushFormGroupToTarget(
          val as CodeableConceptValueDto & { $ref: string },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          targetFormGroup.get('children') as UntypedFormArray,
        );
      }
    }
    subTarget.push(targetFormGroup);
  };

  component.values.forEach((value: CodeableConceptValueDto & { $ref: string }) => pushFormGroupToTarget(value, target));
  return target.value;
}

export function getCodeableConceptForm(component?: CodeableConceptDto) {
  const alternativeTranslations = new FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>([]);
  const regionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);
  const values = new FormArray<FormGroup<ControlsOf<CodeableConceptValueDto>>>([]);
  if (component) {
    setAlternativeTranslations(component, alternativeTranslations);
    setRegionalTranslations(component, regionalTranslations);
    setCodeableConceptValues(component, values);
  }

  const form = new FormGroup<ControlsOf<CodeableConceptDto>>({
    // eslint-disable-next-line
    title: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null, []),
    // eslint-disable-next-line
    code: new FormControl<string | null>(null, [Validators.required, Validators.minLength(4)]),
    createdAt: new FormControl<null>(null, []),
    updatedAt: new FormControl<null>(null, []),
    defaultLanguageCode: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    alternativeTranslations: alternativeTranslations,
    applyOrder: new FormControl<boolean | null>(null, []),
    createdById: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    createdBy: new FormControl<User | null>(null),
    updatedById: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    updatedBy: new FormControl<User | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    values: values,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    regionalTranslations: regionalTranslations,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    codings: new FormControl<CodingValue[] | null>([]),
  });
  if (component) {
    form.patchValue(component);
  }

  return form;
}

export function getCodeableConceptValueForm(component?: CodeableConceptValueDto): FormGroup<ControlsOf<CodeableConceptValues>> {
  const alternativeTranslations = new FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>([]);
  const regionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);
  if (component) {
    setAlternativeTranslations(component, alternativeTranslations);
    setRegionalTranslations(component, regionalTranslations);
  }
  const form = new FormGroup<ControlsOf<CodeableConceptValues>>({
    codeableConceptCode: new FormControl<string | null>(null),
    code: new FormControl<string | null>(null),
    order: new FormControl<number | null>(0),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    parents: new UntypedFormArray([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: new UntypedFormArray([]),
    defaultLanguageCode: new FormControl<string | null>(null),
    title: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    alternativeTranslations: alternativeTranslations,
    //should have ID, languageCode, title, description, regionalTranslations[]
    displayOrder: new FormControl<string | null>(null),
    findingDefinitionCode: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    regionalTranslations: regionalTranslations,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    codings: new FormControl<CodingValue[] | null>([]),
  });
  if (component) {
    form.patchValue(component);
  }
  return form;
}

export function getDefinitionForm(type: number, component?: ComponentDto): FormGroup<ControlsOf<ComponentDto>> {
  const alternativeTranslations = new FormArray<FormGroup<ControlsOf<ComponentTranslationDto>>>([]);
  const regionalTranslations = new FormArray<FormGroup<ControlsOf<RegionalTranslationDto>>>([]);
  setAlternativeTranslations(component, alternativeTranslations);
  setRegionalTranslations(component, regionalTranslations);
  const shortType = ShortType[ComponentType[type]] as ShortType;
  const codePattern = `^${shortType}-[a-zA-Z0-9_-]*`;
  const form = new FormGroup<ControlsOf<ComponentDto>>({
    // eslint-disable-next-line
    title: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null),
    // eslint-disable-next-line
    code: new FormControl<string | null>(null, [
      // eslint-disable-next-line @typescript-eslint/unbound-method
      Validators.required,
      Validators.minLength(4),
      Validators.pattern(codePattern),
    ]),
    type: new FormControl<ComponentType | null>(null),
    version: new FormControl<string | null>(null),
    approvedDate: new FormControl<null>(null),
    publishDate: new FormControl<null>(null),
    state: new FormControl<ComponentState | null>(null),
    authorId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    author: new FormControl<User | null>(null),
    editorId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    editor: new FormControl<User | null>(null),
    reviewerId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    reviewer: new FormControl<User | null>(null),
    publisherId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    publisher: new FormControl<User | null>(null),
    taxonomyId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    taxonomy: new FormControl<TaxonomyDto | null>(null),
    requestingOrganisationId: new FormControl<string | null>(null),
    createdAt: new FormControl<null>(null),
    updatedAt: new FormControl<null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    titleSynonyms: new FormControl<string[] | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    titleAbbreviations: new FormControl<string[] | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    defaultLanguageCode: new FormControl<string[] | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    alternativeTranslations: alternativeTranslations,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    availableCodeableConcepts: new FormControl<CodeableConceptDto[] | null>([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    selectedCodeableConceptValues: new FormControl<CodeableConceptDto[] | null>([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    regionalTranslations: regionalTranslations,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    componentLinks: new FormControl<ComponentLinkDto[] | null>([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    codings: new FormControl<CodingValue[] | null>([]),
    hasPublishedFollowedByComponent: new FormControl<boolean>(false),
  });

  switch (type) {
    case ComponentType.ActivityDefinition:
      // eslint-disable-next-line
      form.addControl('kind', new FormControl<ActivityRequestResourceType | null>(null, [Validators.required]));
      form.addControl('activityType', new FormControl<ActionType | null>(null, []));
      break;
    case ComponentType.ConditionDefinition:
      break;
    case ComponentType.FindingDefinition:
      // eslint-disable-next-line
      form.addControl('rule', new FormControl<string | null>(null, []));
      form.addControl('rulePrettyfied', new FormControl<string | null>(null, []));
      form.addControl('findingType', new FormControl<FindingType | null>(null, []));
      break;
    case ComponentType.ObservationDefinition:
      form.addControl('unitId', new FormControl<string | null>(null, []));
      form.addControl('unit', new FormControl<UnitDto | null>(null, []));
      form.addControl('molarMass', new FormControl<number | null>(null, []));
      form.addControl('referenceRanges', new FormControl<ReferenceRangeDto[] | null>(null, []));
      // eslint-disable-next-line
      form.addControl('category', new FormControl<ObservationCategory | null>(null, []));
      // eslint-disable-next-line
      form.addControl(
        'permittedDataType',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        new FormControl<ObservationPermittedDataType | null>(null, [Validators.required]),
      );
      form.addControl('codeableConceptCode', new FormControl<string | null>(null, []));
      form.addControl('codeableConcept', new FormControl<CodeableConceptDto | null>(null, []));
      form.addControl('allowedCodeableConceptValues', new FormControl<CodeableConceptValueDto[] | null>(null, []));
      break;
    default:
      break;
  }
  return form;
}

/* --------------------------------------------------------------------------------------------
                                  ProposedComponent
-------------------------------------------------------------------------------------------- */

export function getProposedComponentForm(): FormGroup<ControlsOf<ProposedComponentDto>> {
  return new FormGroup<ControlsOf<ProposedComponentDto>>({
    code: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    title: new FormControl<string | null>(null, []),
    // @ts-ignore
    description: new FormControl<string | null>(null, []),
    followedByComponentCode: new FormControl<string | null>(null, []),
    followedByComponentVersion: new FormControl<string | null>(null, []),
    proposingOrganisationId: new FormControl<string | null>(null, []),
    shouldNotBePromoted: new FormControl<boolean | null>(null, []),
    proposedInFlowchartElementType: new FormControl<FlowchartElementType | null>(null, []),
    proposedInGuidelineChapterType: new FormControl<GuidelineChapterType | null>(null, []),
    authorId: new FormControl<string | null>(null, []),
    // @ts-ignore
    author: new FormControl<User | null>(null, []),
    editorId: new FormControl<string | null>(null, []),
    // @ts-ignore
    editor: new FormControl<User | null>(null, []),
    // @ts-ignore
    createdAt: new FormControl<Date | null>(null, []),
    // @ts-ignore
    updatedAt: new FormControl<Date | null>(null, []),
  });
}

/* --------------------------------------------------------------------------------------------
                                  getTextChapterForm
-------------------------------------------------------------------------------------------- */

export type ControlsOf<T extends Record<string, any>> = {
  [K in keyof T]: T[K] extends Record<any, any> ? FormGroup<ControlsOf<T[K]>> : FormControl<T[K]>;
};
export type CodeableConceptValues = CodeableConceptValueDto & { displayOrder: string };

export function getTextChapterForm(children?: UntypedFormArray): FormGroup<ControlsOf<NewGuidelineChapter>> {
  return new FormGroup<ControlsOf<NewGuidelineChapter>>({
    id: new FormControl<string | null>(null),
    uniqueId: new FormControl<string>(null),
    type: new FormControl<GuidelineChapterType | null>(null),
    parentChapterId: new FormControl<string | null>(null),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    title: new FormControl<string | null>(null, [Validators.required]),
    // eslint-disable-next-line
    descriptionWidgetId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children || new UntypedFormArray([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment[] | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    bibliographies: new FormControl<BibliographyDto[] | null>(null),
  });
}

/* --------------------------------------------------------------------------------------------
                                  ConditionsChapter
-------------------------------------------------------------------------------------------- */

export function getGuidelineConditionForm(condition: GuidelineConditionDto): FormGroup<ControlsOf<GuidelineConditionDto>> {
  const children = new UntypedFormArray([]);
  if (condition.children) {
    condition.children.forEach((c) => {
      children.push(getGuidelineComponentForm(c));
    });
  }

  return new FormGroup<ControlsOf<NewGuidelineChapter>>({
    id: new FormControl<string | null>(null),
    uniqueId: new FormControl<string>(null),
    // eslint-disable-next-line
    title: new FormControl<string | null>(null),
    // eslint-disable-next-line
    description: new FormGroup({
      text: new FormControl<string | null>(null, []),
      uniqueId: new FormControl<string | null>(null, []),
    }),
    guidelineChapterId: new FormControl<string | null>(null),
    conditionDefinitionCode: new FormControl<string | null>(null),
    conditionDefinitionVersion: new FormControl<string | null>(null),
    conditionDefinition: condition.conditionDefinition
      ? getConditionDefinitionForm(condition.conditionDefinition)
      : new FormControl<ConditionDefinitionDto | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<OrganisationAttachmentDto[] | null>(null),
    codeableConceptValues: new FormControl<CodeableConceptValueDto[] | null>(null),
    componentType: new UntypedFormControl(GuidelineComponentType.Condition, []),
  });
}

export function getConditionDefinitionForm(condition: ConditionDefinitionDto): FormGroup<ControlsOf<ConditionDefinitionDto>> {
  return getDefinitionForm(ComponentType.ConditionDefinition, condition);
}

/* --------------------------------------------------------------------------------------------
                                  GuidelineCriteriaChapterDto
-------------------------------------------------------------------------------------------- */
export function getCriteriaChapterForm(children?: UntypedFormArray): FormGroup<ControlsOf<NewGuidelineChapter>> {
  return new FormGroup<ControlsOf<NewGuidelineChapter>>({
    id: new FormControl<string | null>(null),
    uniqueId: new FormControl<string>(null),
    type: new FormControl<GuidelineChapterType | null>(null),
    parentChapterId: new FormControl<string | null>(null),
    // eslint-disable-next-line
    title: new FormControl<string | null>(null, [Validators.required]),
    // eslint-disable-next-line
    descriptionWidgetId: new FormControl<string | null>(null, []),
    descriptionBelowWidgetId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineComponents: new UntypedFormArray([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children || new UntypedFormArray([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment[] | null>(null),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    bibliographies: new FormControl<BibliographyDto[] | null>(null),
  });
}

export function getGuidelineCriteriaForm(criteria: GuidelineCriterionDto): UntypedFormGroup {
  const children = new UntypedFormArray([]);

  if (criteria.children) {
    criteria.children.forEach((c) => {
      children.push(getGuidelineComponentForm(c));
    });
  }

  return new FormGroup<ControlsOf<GuidelineCriterionDto>>({
    id: new UntypedFormControl(null, []),
    uniqueId: new FormControl<string>(null, []),
    // eslint-disable-next-line
    title: new UntypedFormControl(null, []),
    description: new UntypedFormControl(null, []),
    guidelineChapterId: new UntypedFormControl(null, []),
    parentId: new UntypedFormControl(null, []),
    findingDefinitionCode: new UntypedFormControl(null, []),
    findingDefinitionVersion: new UntypedFormControl(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    findingDefinition: criteria.findingDefinition
      ? getDefinitionForm(ComponentType.FindingDefinition, criteria.findingDefinition)
      : new FormControl<FindingDefinitionDto | null>(null, []),
    rule: new UntypedFormControl(null, []),
    rulePrettyfied: new UntypedFormControl(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new UntypedFormControl([], []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    codeableConceptValues: new UntypedFormControl(null, []),
    componentType: new UntypedFormControl(GuidelineComponentType.Criteria, []),
  });
}

export function getGuidelineObservationForm(criteria: GuidelineObservationDto): UntypedFormGroup {
  const children = new UntypedFormArray([]);
  if (criteria.children) {
    criteria.children.forEach((c) => {
      children.push(getGuidelineComponentForm(c));
    });
  }

  return new UntypedFormGroup({
    id: new UntypedFormControl(null, []),
    uniqueId: new FormControl<string>(null, []),
    // eslint-disable-next-line
    title: new UntypedFormControl(null, []),
    description: new UntypedFormControl(null, []),
    guidelineChapterId: new UntypedFormControl(null, []),
    parentId: new UntypedFormControl(null, []),
    observationDefinitionCode: new UntypedFormControl(null, []),
    observationDefinitionVersion: new UntypedFormControl(null, []),
    observationDefinition: criteria.observationDefinition
      ? getDefinitionForm(ComponentType.ObservationDefinition, criteria.observationDefinition)
      : null,
    children: children,
    attachments: new UntypedFormControl([], []),
    codeableConceptValues: new UntypedFormControl(null, []),
    componentType: new UntypedFormControl(GuidelineComponentType.Observation, []),
  });
}

/* --------------------------------------------------------------------------------------------
                                  PlanChapter
-------------------------------------------------------------------------------------------- */

export function getPlanChapterForm(
  children?: UntypedFormArray,
  guidelineActionsForm?: UntypedFormArray,
): FormGroup<ControlsOf<NewGuidelineChapter>> {
  return new FormGroup<ControlsOf<NewGuidelineChapter>>({
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string | null>(null, []),
    type: new FormControl<GuidelineChapterType | null>(null, []),
    parentChapterId: new FormControl<string | null>(null, []),

    // eslint-disable-next-line
    title: new FormControl<string | null>(null, [Validators.required]),

    // eslint-disable-next-line
    descriptionWidgetId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line
    descriptionBelowWidgetId: new FormControl<string | null>(null, []),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineComponents: guidelineActionsForm || new UntypedFormArray([]),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children || new UntypedFormArray([]),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    bibliographies: new FormControl<BibliographyDto | null>(null, []),
  });
}

export function getPlanActionForm(action: GuidelineActionDto): FormGroup<ControlsOf<GuidelineActionDto>> {
  const children = new UntypedFormArray([]);
  if (action.children) {
    action.children.forEach((c) => {
      children.push(getGuidelineComponentForm(c));
    });
  }

  const outgoingRelationships = new UntypedFormArray([]);

  return new FormGroup<ControlsOf<GuidelineActionDto>>({
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    componentType: new FormControl<GuidelineComponentType | null>(null, []),
    activityDefinitionCode: new FormControl<string | null>(null, []),
    activityDefinitionVersion: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    activityDefinition: action.activityDefinition
      ? getDefinitionForm(ComponentType.ActivityDefinition, action.activityDefinition)
      : new FormControl<ActivityDefinitionDto | null>(null, []),
    significance: new FormControl<GuidelineActionSignificance | null>(null, []),
    priority: new FormControl<GuidelineActionPriority | null>(null, []),
    argument: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    selectionBehaviorForChildren: new FormControl<SelectionBehavior | null>(null, [Validators.required]),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    selectionBehaviorForSiblings: new FormControl<SelectionBehavior | null>(null, [Validators.required]),
    isFollowUp: new FormControl<boolean | null>(null, []),
    parentId: new FormControl<string | null>(null, []),
    title: new FormControl<string | null>(null, []),
    description: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    outgoingActionRelationships: outgoingRelationships,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),
    preferredOptionOrder: new FormControl<number | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    codeableConceptValues: new FormControl<CodeableConceptValueDto[] | null>(null, []),
    medicationGroupId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    medicationRequests: new FormControl<GuidelineActionDto[] | null>(null, []),
  });
}

/* --------------------------------------------------------------------------------------------
                                  TherapyPlanChapter
-------------------------------------------------------------------------------------------- */

export function getTherapyPlanChapterForm(children?: UntypedFormArray): UntypedFormGroup {
  return getPlanChapterForm(children);
}

/* --------------------------------------------------------------------------------------------
                                  getReferenceRangeForm
-------------------------------------------------------------------------------------------- */
export function getReferenceRangeForm(): FormGroup<ControlsOf<ReferenceRangeDto>> {
  return new FormGroup<ControlsOf<ReferenceRangeDto>>({
    low: new FormControl<number | null>(null, []),
    high: new FormControl<number | null>(null, []),
    appliesTo: new FormControl<string | null>(null, []),
    appliesToPrettyfied: new FormControl<string | null>(null, []),
  });
}

/* --------------------------------------------------------------------------------------------
                                  getAttachmentForm
-------------------------------------------------------------------------------------------- */

export function getAttachmentForm(): UntypedFormGroup {
  return new UntypedFormGroup({
    id: new UntypedFormControl(null, []),
    displayName: new UntypedFormControl(null, []),
    file: new UntypedFormControl(null, []),
  });
}

export function getBibliographyForm(): UntypedFormGroup {
  return new UntypedFormGroup({
    file: new UntypedFormControl(null, []),
  });
}

export function castAsFormControl(control: AbstractControl): UntypedFormControl {
  return control as UntypedFormControl;
}

/* --------------------------------------------------------------------------------------------
                                  Flowchart entities
-------------------------------------------------------------------------------------------- */

export function getNewFlowchartStartNodeForm(): FormGroup<ControlsOf<FlowchartStartNodeDto>> {
  return new FormGroup<ControlsOf<FlowchartStartNodeDto>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),
    elementType: new FormControl<FlowchartElementType | null>(null, []),
    guidelineChapterId: new FormControl<string | null>(null, []),
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    positionX: new FormControl<number | null>(null, []),
    positionY: new FormControl<number | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    outgoingLinks: new FormControl<FlowchartLinkDto | null>(null, []),
    width: new FormControl<number | null>(null, []),
    height: new FormControl<number | null>(null, []),
    guidelineCriteriaId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineCriteria: new FormControl<GuidelineCriterionDto | null>(null, []),
    guidelineActionId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineAction: new FormControl<GuidelineActionDto | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    title: new FormControl<string | null>(null, []),
    rule: new FormControl<string | null>(null, []),
    rulePrettyfied: new FormControl<string | null>(null, []),
    showDescription: new FormControl<boolean>(false),
  });
}

export function getNewFlowchartActionForm(): FormGroup<ControlsOf<FlowchartActionDto>> {
  return new FormGroup<ControlsOf<FlowchartActionDto>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),
    elementType: new FormControl<FlowchartElementType | null>(null, []),
    guidelineChapterId: new FormControl<string | null>(null, []),
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    positionX: new FormControl<number | null>(null, []),
    positionY: new FormControl<number | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    outgoingLinks: new FormControl<FlowchartLinkDto | null>(null, []),
    width: new FormControl<number | null>(null, []),
    height: new FormControl<number | null>(null, []),
    guidelineActionId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineAction: new FormControl<GuidelineActionDto | null>(null, []),
    // @ts-ignore
    children: new FormControl<FlowchartNodeDto[] | null>(null, []),
    showDescription: new FormControl<boolean>(false),
  });
}

export function getNewSelectionBehaviorForm(): FormGroup<ControlsOf<FlowchartSelectionBehaviorDto>> {
  return new FormGroup<ControlsOf<FlowchartSelectionBehaviorDto>>({
    elementType: new FormControl<FlowchartElementType | null>(null, []),
    guidelineChapterId: new FormControl<string | null>(null, []),
    id: new FormControl<string | null>(null, []),
    positionX: new FormControl<number | null>(null, []),
    positionY: new FormControl<number | null>(null, []),
    width: new FormControl<number | null>(null, []),
    height: new FormControl<number | null>(null, []),
    selectionBehavior: new FormControl<SelectionBehavior | null>(null, []),
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    outgoingLinks: new FormControl<FlowchartLinkDto | null>(null, []),
  });
}

export function getNewFlowchartDecisionForm(): FormGroup<ControlsOf<FlowchartDecisionNodeDto>> {
  return new FormGroup<ControlsOf<FlowchartDecisionNodeDto>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),
    elementType: new FormControl<FlowchartElementType | null>(null, []),
    guidelineChapterId: new FormControl<string | null>(null, []),
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    positionX: new FormControl<number | null>(null, []),
    positionY: new FormControl<number | null>(null, []),
    width: new FormControl<number | null>(null, []),
    height: new FormControl<number | null>(null, []),
    selectionBehavior: new FormControl<SelectionBehavior | null>(null, []),
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    outgoingLinks: new FormControl<FlowchartLinkDto | null>(null, []),
    guidelineCriteriaId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineCriteria: new FormControl<GuidelineCriterionDto | null>(null, []),
    guidelineObservationId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    guidelineObservation: new FormControl<GuidelineObservationDto | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    rule: new FormControl<string | null>(null, []),
    rulePrettyfied: new FormControl<string | null>(null, []),
    ruleTitle: new FormControl<string | null>(null, []),
    ruleDescription: new FormControl<string | null>(null, []),
    showDescription: new FormControl<boolean>(false),
  });
}

export function getNewFlowchartTextBlockForm(): FormGroup<ControlsOf<FlowchartTextBlockDto>> {
  return new FormGroup<ControlsOf<FlowchartTextBlockDto>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>(null, []),
    elementType: new FormControl<FlowchartElementType | null>(null, []),
    guidelineChapterId: new FormControl<string | null>(null, []),
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    positionX: new FormControl<number | null>(null, []),
    positionY: new FormControl<number | null>(null, []),
    width: new FormControl<number | null>(null, []),
    height: new FormControl<number | null>(null, []),
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    content: new FormControl<string | null>(null, []),
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    outgoingLinks: new FormControl<FlowchartLinkDto | null>(null, []),
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/unbound-method
    customStyling: new FormControl<FlowchartNodeStyleDto | null>(null, []),
  });
}

export function getNewFlowchartChapterForm(
  children?: UntypedFormArray,
  flowchartElements?: UntypedFormArray,
  includeNoFormControl?: string,
): FormGroup<ControlsOf<NewGuidelineChapter>> {
  const form = new FormGroup<ControlsOf<NewGuidelineChapter>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachments: new FormControl<Attachment | null>([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    bibliographies: new FormControl<BibliographyDto | null>([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: children || new UntypedFormArray([]),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    flowchartElements: flowchartElements || new FormArray([]),
    id: new FormControl<string | null>(null, []),
    uniqueId: new FormControl<string>(null, []),
    parentChapterId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    title: new FormControl<string | null>(null, [Validators.required]),
    descriptionWidgetId: new FormControl<string | null>(null, []),
    // eslint-disable-next-line
    descriptionBelowWidgetId: new FormControl<string | null>(null, []),
    type: new FormControl<GuidelineChapterType | null>(null, []),
  });
  if (includeNoFormControl) {
    form.addControl('no', new FormControl<string[] | null>(null, []));
  }

  return form;
}

export function getGuidelineComponentForm(guidelineComponent: GuidelineComponentDto): UntypedFormGroup {
  switch (guidelineComponent.componentType) {
    case GuidelineComponentType.Action:
      return getPlanActionForm(guidelineComponent);
    case GuidelineComponentType.Condition:
      return getGuidelineConditionForm(guidelineComponent);
    case GuidelineComponentType.Criteria:
      return getGuidelineCriteriaForm(guidelineComponent);
    case GuidelineComponentType.Observation:
      return getGuidelineObservationForm(guidelineComponent);
  }
}
